import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  serverTimestamp,
  doc,
  deleteDoc,
  writeBatch
} from 'firebase/firestore';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Listen for auth state changes
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(user => {
      setCurrentUser(user);
      if (user) {
        // Load notifications from Firebase when user is authenticated
        loadNotificationsFromFirebase(user.uid);
      } else {
        setNotifications([]);
        setNotificationCount(0);
        setIsLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, []);

  // Load notifications from Firebase
  const loadNotificationsFromFirebase = async (userId) => {
    try {
      setIsLoading(true);
      const notificationsColRef = collection(db, 'users', userId, 'notifications');
      const q = query(notificationsColRef, orderBy('time', 'desc'), limit(50));
      const querySnapshot = await getDocs(q);
      
      const notificationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        time: doc.data().time?.toDate() || new Date() // Convert Firestore timestamp to JS Date
      }));
      
      // Count unread notifications
      const unreadCount = notificationsData.filter(notification => notification.read === false).length;
      
      setNotifications(notificationsData);
      setNotificationCount(unreadCount); // Set count to number of unread notifications
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading notifications:', error);
      setIsLoading(false);
    }
  };

  // Save notification to Firebase
  const saveNotificationToFirebase = async (notification) => {
    if (!currentUser) return;
    
    try {
      const notificationsColRef = collection(db, 'users', currentUser.uid, 'notifications');
      
      // First, check how many notifications we already have
      const q = query(notificationsColRef, orderBy('time', 'asc'));
      const querySnapshot = await getDocs(q);
      
      // If we have 50 or more notifications, delete the oldest ones
      if (querySnapshot.size >= 50) {
        const batch = writeBatch(db);
        const notificationsToDelete = querySnapshot.docs.slice(0, querySnapshot.size - 49);
        
        notificationsToDelete.forEach(docToDelete => {
          batch.delete(doc(db, 'users', currentUser.uid, 'notifications', docToDelete.id));
        });
        
        await batch.commit();
      }
      
      // Add the new notification
      await addDoc(notificationsColRef, {
        ...notification,
        time: serverTimestamp(),
        read: false
      });
      
      // No need to reload here, local state updated optimistically in notify()
    } catch (error) {
      console.error('Error saving notification:', error);
    }
  };

  const notify = useCallback((message, options = {}) => {
    const { variant = 'info', autoHideDuration = 5000, ...rest } = options;
    
    const key = enqueueSnackbar(message, {
      variant,
      autoHideDuration,
      ...rest
    });
    
    const newNotification = {
      message,
      options: { variant, ...rest },
      time: new Date()
    };
    
    // Add to local state, keeping only the 50 most recent notifications
    setNotifications(prev => [newNotification, ...prev].slice(0, 50));
    setNotificationCount(prev => prev + 1);
    
    // Save to Firebase
    saveNotificationToFirebase(newNotification);
    
    return key;
  }, [enqueueSnackbar, currentUser]);

  const dismissNotification = useCallback((notificationId) => {
    closeSnackbar(notificationId);
    
    // Remove from local state
    setNotifications(prev => prev.filter(notification => notification.id !== notificationId));
    
    // Remove from Firebase
    if (currentUser && notificationId) {
      try {
        const notificationDocRef = doc(db, 'users', currentUser.uid, 'notifications', notificationId);
        deleteDoc(notificationDocRef).catch(error => {
          console.error('Error deleting notification:', error);
        });
      } catch (error) {
        console.error('Error with notification reference:', error);
      }
    }
  }, [closeSnackbar, currentUser]);

  const clearAllNotifications = useCallback(() => {
    closeSnackbar();
    setNotifications([]);
    
    // Clear all notifications from Firebase
    if (currentUser) {
      const notificationsColRef = collection(db, 'users', currentUser.uid, 'notifications');
      getDocs(notificationsColRef).then(querySnapshot => {
        querySnapshot.forEach(docSnapshot => {
          deleteDoc(doc(db, 'users', currentUser.uid, 'notifications', docSnapshot.id))
            .catch(error => {
              console.error('Error deleting notification:', error);
            });
        });
      }).catch(error => {
        console.error('Error getting notifications:', error);
      });
    }
  }, [closeSnackbar, currentUser]);

  // Alias for clearAllNotifications for compatibility
  const clearNotifications = clearAllNotifications;

  const resetNotificationCount = useCallback(async () => {
    setNotificationCount(0);
    
    // Mark all notifications as read in Firebase
    if (currentUser) {
      try {
        const notificationsColRef = collection(db, 'users', currentUser.uid, 'notifications');
        const querySnapshot = await getDocs(notificationsColRef);
        
        // Update each unread notification to be marked as read
        const batch = writeBatch(db);
        querySnapshot.docs.forEach(docSnapshot => {
          if (docSnapshot.data().read === false) {
            const docRef = doc(db, 'users', currentUser.uid, 'notifications', docSnapshot.id);
            batch.update(docRef, { read: true });
          }
        });
        
        // Commit the batch
        await batch.commit();
        
        // Update local state
        setNotifications(prev => prev.map(notification => ({
          ...notification,
          read: true
        })));
      } catch (error) {
        console.error('Error marking notifications as read:', error);
      }
    }
  }, [currentUser]);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(() => ({
    notifications,
    notificationCount,
    currentUser,
    notify,
    dismissNotification,
    clearAllNotifications,
    clearNotifications, // Alias
    resetNotificationCount,
    setNotifications, // Allow direct manipulation if needed, though risky
    isLoading
  }), [
    notifications,
    notificationCount,
    currentUser,
    notify,
    dismissNotification,
    clearAllNotifications,
    clearNotifications, // Alias dependency
    resetNotificationCount,
    isLoading
    // setNotifications is stable and doesn't need to be listed
  ]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  
  return context;
};